/**
 * Query Utilities
 *
 * Utilities for TanStack Query operations with improved reliability.
 * Provides cache invalidation that marks queries as stale without
 * immediately refetching, reducing duplicate network requests.
 */

import type { QueryClient, QueryKey } from "@tanstack/react-query";

/**
 * Result of an invalidation operation
 */
export interface InvalidationResult {
  /** Whether the invalidation completed successfully */
  success: boolean;
  /** Whether the operation timed out (kept for backwards compatibility) */
  timedOut: boolean;
}

/**
 * Invalidate queries by marking them as stale.
 *
 * Uses `queryClient.invalidateQueries()` which marks queries as stale
 * without immediately refetching. Stale queries will be refetched when:
 * - The component using them re-renders
 * - The user navigates to a view that uses them
 * - refetchOnWindowFocus triggers (if enabled)
 *
 * This approach reduces duplicate network requests compared to refetchQueries().
 *
 * @param queryClient - The TanStack Query client instance
 * @param queryKey - The query key to invalidate (supports partial matching)
 * @param _timeoutMs - Deprecated, kept for backwards compatibility
 * @returns Promise resolving to the invalidation result
 *
 * @example
 * ```typescript
 * // Basic usage - marks bookings queries as stale
 * await invalidateWithTimeout(queryClient, ["bookings"]);
 *
 * // Queries will refetch automatically when next accessed
 * ```
 */
export async function invalidateWithTimeout(
  queryClient: QueryClient,
  queryKey: QueryKey,
): Promise<InvalidationResult> {
  try {
    // Invalidate queries - marks them as stale without immediate refetch
    // Only currently active (mounted) queries will refetch automatically
    await queryClient.invalidateQueries({
      queryKey,
      refetchType: "active", // Only refetch queries that are currently mounted
    });

    return { success: true, timedOut: false };
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error(
        "[queryUtils] Invalidation failed for key:",
        queryKey,
        error,
      );
    }
    return { success: false, timedOut: false };
  }
}

/**
 * Create a reusable invalidation function for a specific query key.
 *
 * Useful in hooks where you want a stable callback reference.
 *
 * @param queryClient - The TanStack Query client instance
 * @param queryKey - The query key to invalidate
 * @returns Async function that performs the invalidation
 *
 * @example
 * ```typescript
 * const invalidateBookings = useCallback(
 *   () => createInvalidator(queryClient, ["bookings"])(),
 *   [queryClient]
 * );
 * ```
 */
export function createInvalidator(
  queryClient: QueryClient,
  queryKey: QueryKey,
): () => Promise<InvalidationResult> {
  return () => invalidateWithTimeout(queryClient, queryKey);
}
